import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Hide, Show } from "react-iconly";

import {
  createBrokerRequest,
  sendBrokerOtp,
  verifyBrokerOtp,
  login,
} from "../../services/auth.service";
import { loginr, selectUser } from "../../slice/authSlice";
import { toast_success } from "../../config/Constant";
import "./ServiceRequestFlow.scss";

/**
 * ServiceRequestFlow - inline, in-place guided flow that lives in the hero.
 *
 * Logged in:   describe -> send -> success
 * Logged out:  describe -> about you -> password -> verify email -> auto-login -> success
 *
 * Steps are kept short and uniform so the card height barely changes between
 * them. Every submission lands in the admin "Broker Overview" via /api/broker.
 */

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const passwordChecks = (v) => [
  { label: "8+ characters", ok: v.length >= 8 },
  { label: "Uppercase", ok: /[A-Z]/.test(v) },
  { label: "Lowercase", ok: /[a-z]/.test(v) },
  { label: "Number", ok: /[0-9]/.test(v) },
  { label: "Symbol", ok: /[!@#$%^&*(),.?":{}|<>]/.test(v) },
];

const ServiceRequestFlow = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  // 1 describe · 2 about you · 3 password · 4 verify · 99 success
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];

  const [form, setForm] = useState({
    description: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: user.firstName || "",
        email: user.email || "",
        phone: user.phone || "",
      }));
    }
  }, [user]);

  const totalSteps = user ? 1 : 4;
  const go = (next) => {
    setDir(next >= step ? 1 : -1);
    setStep(next);
  };
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  // ---- Step 1: describe ----------------------------------------------
  const submitDescribe = async () => {
    if (!form.description.trim()) {
      setErrors({ description: "Tell us a little about what you need" });
      return;
    }
    if (user) {
      setLoading(true);
      const res = await createBrokerRequest({
        description: form.description.trim(),
        name: form.name,
        email: form.email,
        phone: form.phone,
        userId: user.id,
      });
      setLoading(false);
      if (res?.data?.status) go(99);
    } else {
      go(2);
    }
  };

  // ---- Step 2: about you ---------------------------------------------
  const submitBasics = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name, please";
    if (!emailOk(form.email)) e.email = "Enter a valid email";
    if (form.phone && form.phone.length > 10) e.phone = "Max 10 digits";
    setErrors(e);
    if (Object.keys(e).length) return;
    go(3);
  };

  // ---- Step 3: password → send OTP -----------------------------------
  const submitPassword = async () => {
    const e = {};
    if (passwordChecks(form.password).some((c) => !c.ok))
      e.password = "Password doesn't meet the requirements";
    if (form.confirm !== form.password) e.confirm = "Passwords don't match";
    setErrors(e);
    if (Object.keys(e).length) return;

    setLoading(true);
    const res = await sendBrokerOtp(form.email);
    setLoading(false);
    if (res?.data?.status) {
      toast.success("We've emailed you a 4-digit code", toast_success);
      setOtp(["", "", "", ""]);
      go(4);
      setTimeout(() => otpRefs[0].current?.focus(), 120);
    } else {
      toast.error(res?.data?.message || "Couldn't send the code, please retry");
    }
  };

  // ---- Step 4: verify → create + auto-login --------------------------
  const submitOtp = async (codeArr) => {
    const code = (codeArr || otp).join("");
    if (code.length !== 4) {
      toast.error("Enter the full 4-digit code");
      return;
    }
    setLoading(true);

    const verify = await verifyBrokerOtp(form.email, code);
    if (!verify?.data?.status) {
      setLoading(false);
      toast.error(verify?.data?.message || "That code didn't match");
      return;
    }

    const res = await createBrokerRequest({
      description: form.description.trim(),
      name: form.name,
      email: form.email,
      phone: form.phone,
      password: form.password,
      createAccount: true,
      otp: code,
    });
    if (!res?.data?.status) {
      setLoading(false);
      return;
    }

    const loginRes = await login({
      email: form.email,
      password: form.password,
      deviceType: "web",
    });
    if (loginRes?.data?.status) {
      dispatch(loginr(loginRes.data));
      localStorage.setItem("accessToken", loginRes.data.token);
    }
    setLoading(false);
    go(99);
  };

  const resendOtp = async () => {
    const res = await sendBrokerOtp(form.email);
    if (res?.data?.status) toast.success("New code sent", toast_success);
  };

  const reset = () => {
    setDir(-1);
    setStep(1);
    setOtp(["", "", "", ""]);
    setErrors({});
    setForm((f) => ({ ...f, description: "", password: "", confirm: "" }));
  };

  const onOtpChange = (i, v) => {
    if (!/^[0-9]?$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 3) otpRefs[i + 1].current?.focus();
    // Auto-verify the moment all four digits are in.
    if (next.every((d) => d) && !loading) submitOtp(next);
  };
  const onOtpKey = (i, ev) => {
    if (ev.key === "Backspace" && !otp[i] && i > 0) otpRefs[i - 1].current?.focus();
  };

  // Enter advances the current step.
  const onEnter = (fn) => (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      fn();
    }
  };

  const pwChecks = passwordChecks(form.password);

  return (
    <div className="srf">
      {/* progress */}
      {step !== 99 && (
        <div className="srf__progress" aria-hidden>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span key={i} className={`srf__seg ${i < step ? "is-on" : ""}`} />
          ))}
        </div>
      )}

      <div className="srf__viewport">
        <div className="srf__panel" key={step} data-dir={dir}>
          {/* STEP 1 - describe */}
          {step === 1 && (
            <>
              <h2 className="srf__title">What do you need done?</h2>
              <p className="srf__sub">
                Tell us in a sentence. We'll match you with the right Helpr.
              </p>
              <div className="srf__field">
                <textarea
                  className={`srf__input srf__textarea ${errors.description ? "is-bad" : ""}`}
                  rows={3}
                  autoFocus
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="e.g. Fix a leaking kitchen tap this week"
                />
                {errors.description && <span className="srf__err">{errors.description}</span>}
              </div>
              <button className="srf__btn" onClick={submitDescribe} disabled={loading}>
                {loading ? "Sending…" : user ? "Send request" : "Continue"}
              </button>
              <p className="srf__note">Takes under a minute. No spam, ever.</p>
            </>
          )}

          {/* STEP 2 - about you */}
          {step === 2 && (
            <>
              <h2 className="srf__title">A few details</h2>
              <p className="srf__sub">So we can reach you and match you faster.</p>

              <div className="srf__field">
                <label className="srf__label">Full name</label>
                <input
                  className={`srf__input ${errors.name ? "is-bad" : ""}`}
                  autoFocus
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  onKeyDown={onEnter(submitBasics)}
                  placeholder="Jane Smith"
                />
                {errors.name && <span className="srf__err">{errors.name}</span>}
              </div>

              <div className="srf__field">
                <label className="srf__label">Email</label>
                <input
                  className={`srf__input ${errors.email ? "is-bad" : ""}`}
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  onKeyDown={onEnter(submitBasics)}
                  placeholder="jane@email.com"
                />
                {errors.email && <span className="srf__err">{errors.email}</span>}
              </div>

              <div className="srf__field">
                <label className="srf__label">
                  Phone <span className="srf__opt">(optional)</span>
                </label>
                <input
                  className={`srf__input ${errors.phone ? "is-bad" : ""}`}
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value.replace(/[^0-9]/g, ""))}
                  onKeyDown={onEnter(submitBasics)}
                  placeholder="4441234"
                />
                {errors.phone && <span className="srf__err">{errors.phone}</span>}
              </div>

              <div className="srf__actions">
                <button className="srf__btn srf__btn--ghost" onClick={() => go(1)} disabled={loading}>
                  Back
                </button>
                <button className="srf__btn" onClick={submitBasics} disabled={loading}>
                  Continue
                </button>
              </div>
            </>
          )}

          {/* STEP 3 - password */}
          {step === 3 && (
            <>
              <h2 className="srf__title">Secure your account</h2>
              <p className="srf__sub">Choose a password to finish signing up.</p>

              <div className="srf__field">
                <label className="srf__label">Password</label>
                <div className="srf__pw">
                  <input
                    className={`srf__input ${errors.password ? "is-bad" : ""}`}
                    type={showPw ? "text" : "password"}
                    autoFocus
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder="Create a password"
                  />
                  <button type="button" className="srf__eye" onClick={() => setShowPw(!showPw)}>
                    {showPw ? <Show size={18} /> : <Hide size={18} />}
                  </button>
                </div>
                {form.password.length > 0 && (
                  <div className="srf__checks">
                    {pwChecks.map((c) => (
                      <span key={c.label} className={`srf__chip ${c.ok ? "is-ok" : ""}`}>
                        {c.label}
                      </span>
                    ))}
                  </div>
                )}
                {errors.password && <span className="srf__err">{errors.password}</span>}
              </div>

              <div className="srf__field">
                <label className="srf__label">Confirm password</label>
                <div className="srf__pw">
                  <input
                    className={`srf__input ${errors.confirm ? "is-bad" : ""}`}
                    type={showCpw ? "text" : "password"}
                    value={form.confirm}
                    onChange={(e) => set("confirm", e.target.value)}
                    onKeyDown={onEnter(submitPassword)}
                    placeholder="Re-enter password"
                  />
                  <button type="button" className="srf__eye" onClick={() => setShowCpw(!showCpw)}>
                    {showCpw ? <Show size={18} /> : <Hide size={18} />}
                  </button>
                </div>
                {errors.confirm && <span className="srf__err">{errors.confirm}</span>}
              </div>

              <div className="srf__actions">
                <button className="srf__btn srf__btn--ghost" onClick={() => go(2)} disabled={loading}>
                  Back
                </button>
                <button className="srf__btn" onClick={submitPassword} disabled={loading}>
                  {loading ? "Sending code…" : "Continue"}
                </button>
              </div>
            </>
          )}

          {/* STEP 4 - verify */}
          {step === 4 && (
            <>
              <h2 className="srf__title">Check your inbox</h2>
              <p className="srf__sub">
                Enter the 4-digit code we sent to <strong>{form.email}</strong>
              </p>
              <div className="srf__otp">
                {otp.map((v, i) => (
                  <input
                    key={i}
                    ref={otpRefs[i]}
                    className="srf__otpbox"
                    inputMode="numeric"
                    maxLength={1}
                    value={v}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => onOtpKey(i, e)}
                  />
                ))}
              </div>
              <button className="srf__resend" onClick={resendOtp} type="button">
                Didn't get it? Resend code
              </button>
              <div className="srf__actions">
                <button className="srf__btn srf__btn--ghost" onClick={() => go(3)} disabled={loading}>
                  Back
                </button>
                <button className="srf__btn" onClick={submitOtp} disabled={loading}>
                  {loading ? "Verifying…" : "Verify & continue"}
                </button>
              </div>
            </>
          )}

          {/* SUCCESS */}
          {step === 99 && (
            <div className="srf__success">
              <div className="srf__check">
                <svg viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="24" fill="none" />
                  <path fill="none" d="M14 27 l8 8 l16 -16" />
                </svg>
              </div>
              <h2 className="srf__title">Request sent 🎉</h2>
              <p className="srf__sub">
                A Helpr will reach out shortly to get things moving.
                {!user ? " You're signed in and ready to go." : ""}
              </p>
              <button className="srf__btn" onClick={reset}>
                Send another request
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestFlow;
